"use client";

import { useEffect, useState, useRef } from "react";
import { cn } from "@/lib/utils";
import { Sparkles, Wand2, Loader2, Palette, Type } from "lucide-react";

const PROMPT = "A lone knight before a burning castle, cinematic and moody";
const STYLES = ["Anime", "Manhwa", "Painterly", "Cinematic"];
const SELECTED_STYLE = 3;

export function CoverGeneratorDemo() {
  const [step, setStep] = useState(0);
  const [promptChars, setPromptChars] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;

    const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

    async function runAnimation() {
      const isCancelled = () => cancelledRef.current;

      setStep(0);
      setPromptChars(0);
      await wait(600);
      if (isCancelled()) return;

      // Step 1: type the prompt
      setStep(1);
      for (let i = 1; i <= PROMPT.length; i++) {
        if (isCancelled()) return;
        setPromptChars(i);
        await wait(28);
      }
      await wait(400);
      if (isCancelled()) return;

      // Step 2: pick a style
      setStep(2);
      await wait(900);
      if (isCancelled()) return;

      // Step 3: generating (shimmer)
      setStep(3);
      await wait(1900);
      if (isCancelled()) return;

      // Step 4: art reveals
      setStep(4);
      await wait(700);
      if (isCancelled()) return;

      // Step 5: title overlay
      setStep(5);
      await wait(5200);
      if (isCancelled()) return;

      if (!isCancelled()) runAnimation();
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          observer.disconnect();
          runAnimation();
        }
      },
      { threshold: 0.4 }
    );

    if (containerRef.current) observer.observe(containerRef.current);

    return () => {
      cancelledRef.current = true;
      observer.disconnect();
    };
  }, []);

  return (
    <section
      ref={containerRef}
      className="mx-auto flex w-full max-w-6xl flex-col items-center justify-between gap-12 px-6 pt-24 pb-12 md:flex-row lg:px-12"
    >
      {/* ── Visual Demo Container ──────────────── */}
      <div className="relative w-full max-w-2xl flex-1 overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-indigo-900/20 via-background to-purple-900/10 p-4 shadow-2xl before:pointer-events-none before:absolute before:inset-0 before:bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] before:from-indigo-500/10 before:via-transparent before:to-transparent">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] bg-[size:20px_20px] opacity-[0.03]" />

        <div className="relative grid grid-cols-1 gap-4 rounded-2xl border border-border bg-card/90 p-4 shadow-inner backdrop-blur-xl sm:grid-cols-[1fr_auto]">
          {/* Left: controls */}
          <div className="flex flex-col gap-3">
            {/* Prompt */}
            <div>
              <span className="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                <Wand2 className="size-3" /> Describe your cover
              </span>
              <div className="min-h-[64px] rounded-xl border border-border bg-background/70 px-3 py-2 text-sm text-foreground">
                {PROMPT.substring(0, promptChars)}
                {step === 1 && promptChars < PROMPT.length && (
                  <span className="ml-0.5 inline-block h-4 w-[2px] animate-pulse bg-foreground align-middle" />
                )}
              </div>
            </div>

            {/* Style presets */}
            <div>
              <span className="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                <Palette className="size-3" /> Style
              </span>
              <div className="flex flex-wrap gap-1.5">
                {STYLES.map((s, i) => (
                  <span
                    key={s}
                    className={cn(
                      "rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-all duration-300",
                      step >= 2 && i === SELECTED_STYLE
                        ? "border-brand bg-brand/15 text-brand ring-1 ring-brand/40"
                        : "border-border bg-background/70 text-foreground/70"
                    )}
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>

            {/* Generate button */}
            <button
              type="button"
              tabIndex={-1}
              aria-hidden="true"
              className={cn(
                "mt-auto flex items-center justify-center gap-2 rounded-full bg-accent px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-accent/30 transition-all duration-200",
                step === 3 && "scale-95"
              )}
            >
              {step === 3 ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  Generating…
                </>
              ) : (
                <>
                  <Sparkles className="size-4" />
                  Generate cover
                </>
              )}
            </button>
          </div>

          {/* Right: cover preview */}
          <div className="mx-auto w-[190px] shrink-0">
            <div className="relative aspect-[2/3] w-full overflow-hidden rounded-xl border border-border shadow-lg">
              {/* Placeholder before generating */}
              <div
                className={cn(
                  "absolute inset-0 flex items-center justify-center bg-muted/40 transition-opacity duration-500",
                  step >= 4 ? "opacity-0" : "opacity-100"
                )}
              >
                <Sparkles className="size-8 text-muted-foreground/40" />
              </div>

              {/* Shimmer while generating */}
              {step === 3 && (
                <div className="absolute inset-0 z-10 animate-pulse bg-gradient-to-br from-indigo-500/20 via-purple-500/10 to-accent/20" />
              )}

              {/* Generated artwork (inline SVG illustration) */}
              <div
                className={cn(
                  "absolute inset-0 transition-all duration-700",
                  step >= 4
                    ? "scale-100 opacity-100 blur-0"
                    : "scale-105 opacity-0 blur-sm"
                )}
              >
                <svg
                  viewBox="0 0 200 300"
                  className="h-full w-full"
                  preserveAspectRatio="xMidYMid slice"
                  aria-hidden="true"
                >
                  <defs>
                    <linearGradient id="coverSky" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#1e1b4b" />
                      <stop offset="45%" stopColor="#4c1d95" />
                      <stop offset="75%" stopColor="#9a3412" />
                      <stop offset="100%" stopColor="#f97316" />
                    </linearGradient>
                    <radialGradient id="coverMoon" cx="50%" cy="50%" r="50%">
                      <stop offset="0%" stopColor="#faf5ff" />
                      <stop offset="100%" stopColor="#c4b5fd" />
                    </radialGradient>
                    <radialGradient id="coverEmber" cx="50%" cy="100%" r="70%">
                      <stop offset="0%" stopColor="#fb923c" stopOpacity="0.9" />
                      <stop offset="60%" stopColor="#ea580c" stopOpacity="0.25" />
                      <stop offset="100%" stopColor="#ea580c" stopOpacity="0" />
                    </radialGradient>
                  </defs>

                  {/* Sky */}
                  <rect width="200" height="300" fill="url(#coverSky)" />
                  {/* Moon */}
                  <circle cx="140" cy="70" r="34" fill="url(#coverMoon)" opacity="0.9" />
                  <circle cx="140" cy="70" r="34" fill="#4c1d95" opacity="0.12" />
                  {/* Ember glow at base */}
                  <rect y="150" width="200" height="150" fill="url(#coverEmber)" />
                  {/* Castle silhouette */}
                  <g fill="#0b0a1a">
                    <rect x="20" y="205" width="160" height="95" />
                    <rect x="34" y="175" width="18" height="40" />
                    <rect x="148" y="175" width="18" height="40" />
                    <rect x="92" y="150" width="26" height="70" />
                    <polygon points="34,175 43,160 52,175" />
                    <polygon points="148,175 157,160 166,175" />
                    <polygon points="92,150 105,132 118,150" />
                  </g>
                  {/* Lone knight silhouette */}
                  <g fill="#050409">
                    <ellipse cx="100" cy="288" rx="26" ry="8" opacity="0.6" />
                    <rect x="96" y="238" width="8" height="46" rx="3" />
                    <circle cx="100" cy="232" r="7" />
                    <rect x="90" y="236" width="3" height="50" rx="1.5" />
                  </g>
                </svg>

                {/* Bottom scrim for title legibility */}
                <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/80 to-transparent" />

                {/* Title overlay */}
                <div
                  className={cn(
                    "absolute inset-x-0 bottom-0 p-3 text-center transition-all duration-700",
                    step >= 5
                      ? "translate-y-0 opacity-100"
                      : "translate-y-3 opacity-0"
                  )}
                >
                  <div className="font-heading text-base font-extrabold uppercase leading-tight tracking-wide text-white drop-shadow-lg">
                    The Ember Throne
                  </div>
                  <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/80">
                    Elandor
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Text Explainer ─────────────────────── */}
      <div className="max-w-lg flex-1 space-y-6">
        <div className="inline-flex items-center gap-2 rounded-full border border-brand/20 bg-brand/10 px-3 py-1 text-xs font-semibold text-brand">
          <Sparkles className="size-3.5" />
          AI cover art
        </div>
        <h2 className="font-heading text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
          A cover that stops the scroll — in seconds
        </h2>
        <p className="text-lg leading-relaxed text-muted-foreground">
          Describe your story and generate cover art on the spot, then drop your
          title and author name right on top. No designer, no stock-photo hunt.
        </p>

        <ul className="mt-8 space-y-4">
          <li
            className={cn(
              "flex items-start gap-3 transition-opacity duration-700",
              step >= 1 ? "opacity-100" : "opacity-40"
            )}
          >
            <div className="mt-1 shrink-0 rounded-full bg-indigo-500/20 p-1 text-indigo-400">
              <Wand2 className="size-4" />
            </div>
            <div>
              <span className="font-semibold text-foreground">
                Text-to-cover:
              </span>{" "}
              Turn a one-line description into finished art.
            </div>
          </li>
          <li
            className={cn(
              "flex items-start gap-3 transition-opacity duration-700",
              step >= 2 ? "opacity-100" : "opacity-40"
            )}
          >
            <div className="mt-1 shrink-0 rounded-full bg-purple-500/20 p-1 text-purple-400">
              <Palette className="size-4" />
            </div>
            <div>
              <span className="font-semibold text-foreground">
                Styles for every genre:
              </span>{" "}
              Anime, manhwa, painterly, cinematic, and more.
            </div>
          </li>
          <li
            className={cn(
              "flex items-start gap-3 transition-opacity duration-700",
              step >= 5 ? "opacity-100" : "opacity-40"
            )}
          >
            <div className="mt-1 shrink-0 rounded-full bg-accent/20 p-1 text-accent">
              <Type className="size-4" />
            </div>
            <div>
              <span className="font-semibold text-foreground">
                Title &amp; byline built in:
              </span>{" "}
              Position your text with the overlay editor — export-ready.
            </div>
          </li>
        </ul>
      </div>
    </section>
  );
}
